import { useState, useEffect, useRef } from 'react';
import { log } from '@/lib/logger';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/form/select';
import { Slider } from '@/components/ui/form/slider';
import { Switch } from '@/components/ui/form/switch';
import { Input } from '@/components/ui/form/input';
import { Textarea } from '@/components/ui/form/textarea';
import { Loader2, Save, Cpu, AlertTriangle, Target, Heart, Zap, Activity, Dumbbell, Timer } from 'lucide-react';
import { fetchTrainingPreferences, updateTrainingPreferences, updateUserProfile, fetchUserProfile } from '@/lib/api';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useAuth } from '@/context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { getStoredProfile, getOnboardingAdditionalData, saveProfile, saveOnboardingAdditionalData } from '@/lib/storage';
import { toast } from '@/hooks/use-toast';
import type { Sport, AthleteProfile } from '@/types';

const sports = [
  { id: 'running', label: 'RUN' },
  { id: 'cycling', label: 'BIKE' },
  { id: 'swimming', label: 'SWIM' },
  { id: 'triathlon', label: 'TRI' },
  { id: 'crossfit', label: 'CF' },
  { id: 'strength', label: 'LFT' },
  { id: 'hyrox', label: 'HRX' },
  { id: 'walking', label: 'WALK' },
  { id: 'hiking', label: 'HIKE' },
];

const dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Sport-aware philosophy arrays — the LLM selects and interprets these; no hardcoded structure
const ENDURANCE_PHILOSOPHIES = [
  { value: 'jack_daniels', label: 'Jack Daniels', desc: 'VDOT-Based' },
  { value: 'polarized', label: 'Polarized', desc: '80/20 Distribution' },
  { value: 'lydiard', label: 'Lydiard', desc: 'Aerobic Base Focus' },
  { value: 'hanson', label: 'Hanson', desc: 'Cumulative Fatigue' },
  { value: 'adaptive', label: 'Adaptive AI', desc: 'Data-Driven' },
];

const CYCLING_PHILOSOPHIES = [
  { value: 'sweet_spot_base', label: 'Sweet Spot Base', desc: 'FTP-Focused' },
  { value: 'polarized', label: 'Polarized', desc: '80/20 Intensity' },
  { value: 'threshold_progression', label: 'Threshold Prog.', desc: 'FTP Building' },
  { value: 'adaptive', label: 'Adaptive AI', desc: 'Data-Driven' },
];

const CROSSFIT_PHILOSOPHIES = [
  { value: 'general_crossfit', label: 'General CF', desc: 'Balanced GPP' },
  { value: 'competitive_crossfit', label: 'Competitive CF', desc: 'Games-Oriented' },
  { value: 'strength_biased_cf', label: 'Strength CF', desc: 'Strength-Biased' },
  { value: 'metcon_biased_cf', label: 'Conditioning CF', desc: 'MetCon-Heavy' },
];

const STRENGTH_PHILOSOPHIES = [
  { value: 'linear_progression', label: 'Linear Progression', desc: 'Beginner/Novice' },
  { value: 'five_three_one', label: '5/3/1', desc: 'Wendler Method' },
  { value: 'powerlifting_peak', label: 'Powerlifting', desc: 'Meet Prep' },
  { value: 'olympic_lifting', label: 'Olympic Lifting', desc: 'Snatch / C&J' },
  { value: 'hypertrophy_block', label: 'Hypertrophy', desc: 'Muscle Building' },
];

const HYROX_PHILOSOPHIES = [
  { value: 'hyrox_base', label: 'Hyrox Base', desc: 'Capacity Building' },
  { value: 'hyrox_build', label: 'Hyrox Build', desc: 'Event-Specific' },
  { value: 'hyrox_competition', label: 'Hyrox Competition', desc: 'Race Prep' },
];

const HYROX_STATIONS = [
  { id: 'ski_erg', label: 'Ski Erg' },
  { id: 'sled_push', label: 'Sled Push' },
  { id: 'sled_pull', label: 'Sled Pull' },
  { id: 'burpee_broad_jump', label: 'Burpee BJ' },
  { id: 'rowing', label: 'Row Erg' },
  { id: 'farmers_carry', label: 'Farmers Carry' },
  { id: 'sandbag_lunges', label: 'Sandbag Lunges' },
  { id: 'wall_balls', label: 'Wall Balls' },
];

function getPhilosophyOptions(activeSports: Sport[]): Array<{ value: string; label: string; desc: string }> {
  const has = (s: string) => activeSports.some(x => x === s);
  if (has('hyrox')) return HYROX_PHILOSOPHIES;
  if (has('crossfit') && !has('running') && !has('cycling')) return CROSSFIT_PHILOSOPHIES;
  if (has('strength') && !has('running') && !has('cycling') && !has('crossfit')) return STRENGTH_PHILOSOPHIES;
  if (has('cycling') && !has('running')) return CYCLING_PHILOSOPHIES;
  return ENDURANCE_PHILOSOPHIES;
}

const consistencyOptions = [
  { value: 'just_starting', label: 'Just Getting Started' },
  { value: 'occasional', label: 'Training Occasionally' },
  { value: 'consistent', label: 'Training Consistently' },
  { value: 'structured', label: 'Structured For Years' },
];

interface PreferencesState {
  primarySports: Sport[];
  trainingDays: boolean[];
  hoursPerWeek: number;
  trainingFocus: 'race' | 'general';
  hasInjuryHistory: boolean;
  injuryNotes: string;
  trainingAge: number;
  consistency: string;
  philosophy: string;
  goal: string;
  raceDetails: string;
  targetEventName: string;
  targetEventDate: string;
  // Cycling
  ftpWatts: number | null;
  cyclingGoal: string;
  cyclingWeeklyHours: number | null;
  // Running
  runningWeeklyHours: number | null;
  runThresholdPaceMinPerKm: number | null;
  // CrossFit
  crossfitExperience: string;
  crossfitWodPreference: string;
  crossfitWeeklyHours: number | null;
  // Strength
  strengthGoal: string;
  squat1rmKg: number | null;
  deadlift1rmKg: number | null;
  bench1rmKg: number | null;
  // Hyrox
  hyroxCategory: string;
  hyroxWeakStations: string[];
  hyroxTargetFinishMinutes: number | null;
}

// Convert min/km ↔ min/mi for threshold pace display
const KM_TO_MI = 1.60934;

export function TrainingPreferencesSection() {
  const { t } = useTranslation();
  const { status, user } = useAuth();
  const queryClient = useQueryClient();
  const { unitSystem, convertPace } = useUnitSystem();
  const isImperial = unitSystem === 'imperial';
  const paceUnitLabel = isImperial ? 'min/mi' : 'min/km';
  const isAuthenticated = status === 'authenticated' && !!user;

  const [prefs, setPrefs] = useState<PreferencesState>({
    primarySports: [],
    trainingDays: [true, true, true, true, true, false, false],
    hoursPerWeek: 10,
    trainingFocus: 'general',
    hasInjuryHistory: false,
    injuryNotes: '',
    trainingAge: 0,
    consistency: '',
    philosophy: 'adaptive',
    goal: '',
    raceDetails: '',
    targetEventName: '',
    targetEventDate: '',
    // Cycling
    ftpWatts: null,
    cyclingGoal: '',
    cyclingWeeklyHours: null,
    // Running
    runningWeeklyHours: null,
    runThresholdPaceMinPerKm: null,
    // CrossFit
    crossfitExperience: '',
    crossfitWodPreference: '',
    crossfitWeeklyHours: null,
    // Strength
    strengthGoal: '',
    squat1rmKg: null,
    deadlift1rmKg: null,
    bench1rmKg: null,
    // Hyrox
    hyroxCategory: '',
    hyroxWeakStations: [],
    hyroxTargetFinishMinutes: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialPrefs, setInitialPrefs] = useState<PreferencesState | null>(null);

  // Guard: load once on mount, and once more when auth first resolves to authenticated.
  // Prevents re-loads on token refresh or other auth context re-evaluations.
  const hasLoadedRef = useRef(false);
  const wasAuthenticatedRef = useRef(false);

  useEffect(() => {
    const transitioningToAuthenticated = isAuthenticated && !wasAuthenticatedRef.current;
    if (!hasLoadedRef.current || transitioningToAuthenticated) {
      hasLoadedRef.current = true;
      wasAuthenticatedRef.current = isAuthenticated;
      loadPreferences();
    } else {
      wasAuthenticatedRef.current = isAuthenticated;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (initialPrefs) {
      setHasChanges(JSON.stringify(prefs) !== JSON.stringify(initialPrefs));
    }
  }, [prefs, initialPrefs]);

  const loadPreferences = async () => {
    setIsLoading(true);
    try {
      let backendPrefs: Awaited<ReturnType<typeof fetchTrainingPreferences>> | null = null;
      let backendProfile: Awaited<ReturnType<typeof fetchUserProfile>> | null = null;
      if (isAuthenticated) {
        try {
          [backendPrefs, backendProfile] = await Promise.all([
            fetchTrainingPreferences().catch(() => null),
            fetchUserProfile(),
          ]);
        } catch { /* fallback to local */ }
      }

      const profile = getStoredProfile();
      const additionalData = getOnboardingAdditionalData();
      const effectiveProfile = backendProfile || profile;

      let trainingDaysArray: boolean[];
      if (backendPrefs != null) {
        const days = Array.isArray(backendPrefs.available_days) ? backendPrefs.available_days : [];
        trainingDaysArray = weekDays.map(day =>
          days.some((d: string) => String(d).toLowerCase() === day)
        );
      } else if ((effectiveProfile as AthleteProfile)?.weeklyAvailability) {
        const wa = (effectiveProfile as AthleteProfile).weeklyAvailability;
        const daysCount = wa.daysPerWeek ??
            (Array.isArray(wa?.days) ? (wa?.days ?? []).length : 0);
        trainingDaysArray = weekDays.map((_, index) => index < daysCount);
      } else {
        trainingDaysArray = [true, true, true, true, true, false, false];
      }

      const trainingFocus: 'race' | 'general' = backendPrefs
        ? (backendPrefs.training_focus === 'race_focused' ? 'race' : 'general')
        : effectiveProfile?.goals?.some((g: string) => g.toLowerCase().includes('race'))
        ? 'race' : 'general';

      const goal = backendPrefs?.goal || effectiveProfile?.goals?.[0] || '';
      const te = (effectiveProfile as AthleteProfile)?.targetEvent ?? effectiveProfile?.target_event;
      const targetEventName = te?.name ?? '';
      const targetEventDate = te?.date ?? '';
      const raceInput = (effectiveProfile as { race_input?: { details?: string } } | null)?.race_input;
      const raceDetails = raceInput?.details || additionalData?.raceDetails || '';

      const loaded: PreferencesState = {
        primarySports: (backendPrefs?.primary_sports as Sport[]) || (effectiveProfile as AthleteProfile)?.sports || [],
        trainingDays: trainingDaysArray,
        hoursPerWeek: backendPrefs?.weekly_hours || (effectiveProfile as AthleteProfile)?.weeklyAvailability?.hoursPerWeek || 10,
        trainingFocus,
        hasInjuryHistory: backendPrefs?.injury_history || false,
        injuryNotes: backendPrefs?.injury_notes || additionalData?.injuryDetails || '',
        trainingAge: Number(backendPrefs?.years_of_training || (effectiveProfile as AthleteProfile)?.trainingAge) || 0,
        consistency: backendPrefs?.consistency || additionalData?.consistency || '',
        philosophy: backendPrefs?.philosophy ?? 'adaptive',
        goal,
        raceDetails,
        targetEventName,
        targetEventDate,
        // Cycling
        ftpWatts: backendPrefs?.ftp_watts ?? null,
        cyclingGoal: backendPrefs?.cycling_goal || '',
        cyclingWeeklyHours: backendPrefs?.cycling_weekly_hours ?? null,
        // Running
        runningWeeklyHours: backendPrefs?.running_weekly_hours ?? null,
        runThresholdPaceMinPerKm: backendPrefs?.run_threshold_pace_min_per_km ?? null,
        // CrossFit
        crossfitExperience: backendPrefs?.crossfit_experience || '',
        crossfitWodPreference: backendPrefs?.crossfit_wod_preference || '',
        crossfitWeeklyHours: backendPrefs?.crossfit_weekly_hours ?? null,
        // Strength
        strengthGoal: backendPrefs?.strength_goal || '',
        squat1rmKg: backendPrefs?.squat_1rm_kg ?? null,
        deadlift1rmKg: backendPrefs?.deadlift_1rm_kg ?? null,
        bench1rmKg: backendPrefs?.bench_1rm_kg ?? null,
        // Hyrox
        hyroxCategory: backendPrefs?.hyrox_category || '',
        hyroxWeakStations: (backendPrefs?.hyrox_weak_stations as string[]) || [],
        hyroxTargetFinishMinutes: backendPrefs?.hyrox_target_finish_minutes ?? null,
      };
      setPrefs(loaded);
      setInitialPrefs(loaded);
    } catch (error) {
      log.error('SETTINGS', 'Failed to load preferences:', error);
      toast({ title: t('settings.trainingPreferences.toastLoadFailed'), description: t('settings.trainingPreferences.toastLoadFailedDesc'), variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSport = (sportId: string) => {
    setPrefs(prev => {
      const newSports = prev.primarySports.includes(sportId as Sport)
        ? prev.primarySports.filter(s => s !== sportId) as Sport[]
        : [...prev.primarySports, sportId as Sport];
      // Reset philosophy if it is no longer valid for the new sport combination
      const validValues = getPhilosophyOptions(newSports).map(o => o.value);
      const newPhilosophy = prev.philosophy && validValues.includes(prev.philosophy)
        ? prev.philosophy
        : '';
      return { ...prev, primarySports: newSports, philosophy: newPhilosophy };
    });
  };

  const toggleDay = (index: number) => {
    setPrefs(prev => ({
      ...prev,
      trainingDays: prev.trainingDays.map((d, i) => (i === index ? !d : d)),
    }));
  };

  const activeDays = prefs.trainingDays.filter(Boolean).length;
  const isOptimal = activeDays >= 3;

  const handleSave = async () => {
    setIsSaving(true);
    let finalPrefs = prefs;
    let backendSaved = false;
    try {
      if (isAuthenticated) {
        const availableDays = weekDays.filter((_, index) => prefs.trainingDays[index]);
        const trainingFocus: 'race_focused' | 'general_fitness' = prefs.trainingFocus === 'race' ? 'race_focused' : 'general_fitness';

        const saved = await updateTrainingPreferences({
          years_of_training: prefs.trainingAge ?? 0,
          primary_sports: prefs.primarySports,
          available_days: availableDays,
          weekly_hours: prefs.hoursPerWeek,
          training_focus: trainingFocus,
          injury_history: prefs.hasInjuryHistory,
          injury_notes: prefs.injuryNotes || null,
          consistency: prefs.consistency || null,
          philosophy: prefs.philosophy || null,
          goal: prefs.goal || null,
          // Cycling
          ftp_watts: prefs.ftpWatts ?? null,
          cycling_goal: prefs.cyclingGoal || null,
          cycling_weekly_hours: prefs.cyclingWeeklyHours ?? null,
          // Running
          running_weekly_hours: prefs.runningWeeklyHours ?? null,
          run_threshold_pace_min_per_km: prefs.runThresholdPaceMinPerKm ?? null,
          // CrossFit
          crossfit_experience: prefs.crossfitExperience || null,
          crossfit_wod_preference: prefs.crossfitWodPreference || null,
          crossfit_weekly_hours: prefs.crossfitWeeklyHours ?? null,
          // Strength
          strength_goal: prefs.strengthGoal || null,
          squat_1rm_kg: prefs.squat1rmKg ?? null,
          deadlift_1rm_kg: prefs.deadlift1rmKg ?? null,
          bench_1rm_kg: prefs.bench1rmKg ?? null,
          // Hyrox
          hyrox_category: prefs.hyroxCategory || null,
          hyrox_weak_stations: prefs.hyroxWeakStations.length > 0 ? prefs.hyroxWeakStations : null,
          hyrox_target_finish_minutes: prefs.hyroxTargetFinishMinutes ?? null,
        });

        backendSaved = saved != null;
        if (saved?.available_days && Array.isArray(saved.available_days)) {
          const syncedDays = weekDays.map(day =>
            saved.available_days.some((d: string) => d.toLowerCase() === day)
          );
          finalPrefs = { ...prefs, trainingDays: syncedDays };
          setPrefs(finalPrefs);
        }

        const profileUpdate: Partial<AthleteProfile> = {};
        if (prefs.targetEventName || prefs.targetEventDate || prefs.raceDetails) {
          const raceInput = {
            event_name: prefs.targetEventName || '',
            event_date: prefs.targetEventDate || '',
            details: prefs.raceDetails || '',
            source: 'user' as const,
          };
          profileUpdate.targetEvent = prefs.targetEventName || prefs.targetEventDate
            ? { name: prefs.targetEventName, date: prefs.targetEventDate } : null;
          (profileUpdate as Record<string, unknown>).race_input = raceInput;
        } else if (prefs.trainingFocus !== 'race') {
          const currentProfile = await fetchUserProfile();
          if (currentProfile?.target_event) {
            profileUpdate.target_event = currentProfile.target_event;
          } else {
            profileUpdate.target_event = null;
          }
        }
        if (prefs.goal) { profileUpdate.goals = [prefs.goal]; } else { profileUpdate.goals = []; }
        await updateUserProfile(profileUpdate);
      }

      const profile = getStoredProfile() || {
        id: 'local', name: '', sports: [], trainingAge: 0,
        weeklyAvailability: { daysPerWeek: 0, hoursPerWeek: 0 },
        goals: [], stravaConnected: false, onboardingComplete: true,
      };
      const savedActiveDays = finalPrefs.trainingDays.filter(Boolean).length;
      const updatedProfile: AthleteProfile = {
        ...profile,
        sports: finalPrefs.primarySports,
        trainingAge: finalPrefs.trainingAge || 0,
        weeklyAvailability: { daysPerWeek: savedActiveDays, hoursPerWeek: finalPrefs.hoursPerWeek },
        goals: finalPrefs.goal ? [finalPrefs.goal] : [],
        targetEvent: finalPrefs.targetEventName || finalPrefs.targetEventDate
          ? { name: finalPrefs.targetEventName, date: finalPrefs.targetEventDate } : undefined,
      };
      saveProfile(updatedProfile);
      saveOnboardingAdditionalData({
        consistency: finalPrefs.consistency,
        raceDetails: finalPrefs.raceDetails || '',
        injuryDetails: finalPrefs.injuryNotes,
        collectedAt: new Date().toISOString(),
      });

      setInitialPrefs({ ...finalPrefs });
      setHasChanges(false);

      // Update onboarding progress card on dashboard
      queryClient.invalidateQueries({ queryKey: ['profile', 'completeness'] });

      if (backendSaved) {
        toast({ title: t('settings.trainingPreferences.toastEngineUpdated'), description: t('settings.trainingPreferences.toastEngineUpdatedDesc') });
      } else if (isAuthenticated) {
        toast({ title: t('settings.trainingPreferences.toastSaveFailed'), description: t('settings.trainingPreferences.toastSaveFailedServer') });
      } else {
        toast({ title: t('settings.trainingPreferences.toastSavedLocally'), description: t('settings.trainingPreferences.toastSavedLocallyDesc') });
      }
    } catch (error) {
      toast({ title: t('settings.trainingPreferences.toastSaveFailed'), description: error instanceof Error ? error.message : t('settings.trainingPreferences.toastLoadFailedDesc'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
            <Cpu className="h-4 w-4 text-[#00FFC8]" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-foreground">{t('settings.trainingPreferences.title')}</h3>
            <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
              {t('settings.trainingPreferences.subtitle')}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-6">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Cpu className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">{t('settings.trainingPreferences.title')}</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
            {t('settings.trainingPreferences.subtitle')}
          </p>
        </div>
      </div>

      <div className="space-y-6">

        {/* ── SECTION: Discipline ── */}
        <div className="space-y-3">
          <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
            {t('settings.trainingPreferences.sectionDiscipline')}
          </span>
          <div className="flex flex-wrap gap-2">
            {sports.map((sport) => (
              <button
                key={sport.id}
                onClick={() => toggleSport(sport.id)}
                className={cn(
                  'px-4 py-2 rounded-md border font-mono text-xs tracking-wider transition-all duration-200',
                  prefs.primarySports.includes(sport.id as Sport)
                    ? 'border-[#00FFC8]/50 bg-[#00FFC8]/10 text-[#00FFC8] shadow-[0_0_10px_rgba(0,255,200,0.15)]'
                    : 'border-white/[0.1] bg-white/[0.02] text-[#A0A0A0] hover:border-white/[0.2]'
                )}
              >
                {sport.label}
              </button>
            ))}
          </div>
          {prefs.primarySports.length === 0 && (
            <span className="text-[9px] font-mono tracking-[0.12em] uppercase text-[hsl(35,85%,55%)]">
              {t('settings.trainingPreferences.warnNoDiscipline')}
            </span>
          )}
        </div>

        {/* ── SECTION: Cycling FTP (shown only when cycling is selected) ── */}
        {prefs.primarySports.some(s => s === 'cycling') && (
          <div className="space-y-3 pl-3 border-l-2 border-[#00C8FF]/20">
            <div className="flex items-center gap-2">
              <Zap className="h-3 w-3 text-[#00C8FF]" />
              <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
                {t('settings.trainingPreferences.sectionCyclingFtp')}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">
                  {t('settings.trainingPreferences.labelFtpWatts')}
                </span>
                <Input
                  type="number"
                  value={prefs.ftpWatts ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, ftpWatts: val }));
                  }}
                  placeholder="e.g. 230"
                  min={50}
                  max={600}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">
                  {prefs.ftpWatts
                    ? t('settings.trainingPreferences.sysFtpThreshold', { from: Math.round(prefs.ftpWatts * 0.88), to: Math.round(prefs.ftpWatts * 1.05) })
                    : t('settings.trainingPreferences.sysFtpHint')}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">
                  {t('settings.trainingPreferences.labelCyclingFocus')}
                </span>
                <Select
                  value={prefs.cyclingGoal}
                  onValueChange={(v) => setPrefs(p => ({ ...p, cyclingGoal: v }))}
                >
                  <SelectTrigger className="font-mono text-sm border-[#00C8FF]/15 bg-white/[0.03] hover:border-[#00C8FF]/30 transition-colors">
                    <SelectValue placeholder="Select focus" />
                  </SelectTrigger>
                  <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
                    <SelectItem value="gran_fondo" className="font-mono text-sm">{t('settings.trainingPreferences.cyclingFocusGranFondo')}</SelectItem>
                    <SelectItem value="criterium" className="font-mono text-sm">{t('settings.trainingPreferences.cyclingFocusCriterium')}</SelectItem>
                    <SelectItem value="endurance" className="font-mono text-sm">{t('settings.trainingPreferences.cyclingFocusEndurance')}</SelectItem>
                    <SelectItem value="general_fitness" className="font-mono text-sm">{t('settings.trainingPreferences.cyclingFocusGeneral')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION: Running Thresholds (shown only when running is selected) ── */}
        {prefs.primarySports.some(s => s === 'running') && (
          <div className="space-y-3 pl-3 border-l-2 border-[#FF6B6B]/20">
            <div className="flex items-center gap-2">
              <Activity className="h-3 w-3 text-[#FF6B6B]" />
              <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
                {t('settings.trainingPreferences.sectionRunning')}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">
                  {t('settings.trainingPreferences.labelRunWeeklyHours')}
                </span>
                <Input
                  type="number"
                  value={prefs.runningWeeklyHours ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, runningWeeklyHours: val }));
                  }}
                  placeholder="e.g. 8"
                  min={0.5}
                  max={25}
                  step={0.5}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">
                  {prefs.runningWeeklyHours
                    ? t('settings.trainingPreferences.sysRunBudget', { hours: prefs.runningWeeklyHours })
                    : t('settings.trainingPreferences.sysRunBudgetHint')}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">
                  {t('settings.trainingPreferences.labelThresholdPace', { unit: paceUnitLabel })}
                </span>
                <Input
                  type="number"
                  value={prefs.runThresholdPaceMinPerKm != null
                    ? (isImperial
                        ? Math.round(prefs.runThresholdPaceMinPerKm * KM_TO_MI * 100) / 100
                        : prefs.runThresholdPaceMinPerKm)
                    : ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setPrefs(p => ({ ...p, runThresholdPaceMinPerKm: null }));
                    } else {
                      const displayVal = Number(e.target.value);
                      // Convert back to min/km for storage
                      const minPerKm = isImperial ? displayVal / KM_TO_MI : displayVal;
                      setPrefs(p => ({ ...p, runThresholdPaceMinPerKm: Math.round(minPerKm * 100) / 100 }));
                    }
                  }}
                  placeholder={isImperial ? 'e.g. 8.0' : 'e.g. 5.0'}
                  min={isImperial ? 4.8 : 3.0}
                  max={isImperial ? 19.3 : 12.0}
                  step={0.05}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">
                  {prefs.runThresholdPaceMinPerKm
                    ? t('settings.trainingPreferences.sysLtPace', {
                        pace: convertPace(prefs.runThresholdPaceMinPerKm).value.toFixed(2),
                        unit: paceUnitLabel,
                      })
                    : t('settings.trainingPreferences.sysLtPaceHint')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION: CrossFit (shown only when crossfit is selected) ── */}
        {prefs.primarySports.some(s => s === 'crossfit') && (
          <div className="space-y-3 pl-3 border-l-2 border-[#FF9F00]/20">
            <div className="flex items-center gap-2">
              <Activity className="h-3 w-3 text-[#FF9F00]" />
              <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
                {t('settings.trainingPreferences.sectionCrossFit')}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelCfExperience')}</span>
                <Select
                  value={prefs.crossfitExperience}
                  onValueChange={(v) => setPrefs(p => ({ ...p, crossfitExperience: v }))}
                >
                  <SelectTrigger className="font-mono text-sm border-[#FF9F00]/15 bg-white/[0.03] hover:border-[#FF9F00]/30 transition-colors">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
                    <SelectItem value="beginner" className="font-mono text-sm">{t('settings.trainingPreferences.cfBeginner')}</SelectItem>
                    <SelectItem value="intermediate" className="font-mono text-sm">{t('settings.trainingPreferences.cfIntermediate')}</SelectItem>
                    <SelectItem value="advanced" className="font-mono text-sm">{t('settings.trainingPreferences.cfAdvanced')}</SelectItem>
                    <SelectItem value="competition" className="font-mono text-sm">{t('settings.trainingPreferences.cfCompetition')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelWodPreference')}</span>
                <Select
                  value={prefs.crossfitWodPreference}
                  onValueChange={(v) => setPrefs(p => ({ ...p, crossfitWodPreference: v }))}
                >
                  <SelectTrigger className="font-mono text-sm border-[#FF9F00]/15 bg-white/[0.03] hover:border-[#FF9F00]/30 transition-colors">
                    <SelectValue placeholder="Select preference" />
                  </SelectTrigger>
                  <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
                    <SelectItem value="balanced" className="font-mono text-sm">{t('settings.trainingPreferences.wodBalanced')}</SelectItem>
                    <SelectItem value="strength" className="font-mono text-sm">{t('settings.trainingPreferences.wodStrength')}</SelectItem>
                    <SelectItem value="metcon" className="font-mono text-sm">{t('settings.trainingPreferences.wodMetcon')}</SelectItem>
                    <SelectItem value="gymnastics" className="font-mono text-sm">{t('settings.trainingPreferences.wodGymnastics')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelCfWeeklyHours')}</span>
                <Input
                  type="number"
                  value={prefs.crossfitWeeklyHours ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, crossfitWeeklyHours: val }));
                  }}
                  placeholder="e.g. 6"
                  min={0.5}
                  max={25}
                  step={0.5}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">
                  {prefs.crossfitWeeklyHours
                    ? t('settings.trainingPreferences.sysCfBudget', { hours: prefs.crossfitWeeklyHours })
                    : t('settings.trainingPreferences.sysCfBudgetHint')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION: Strength (shown only when strength is selected) ── */}
        {prefs.primarySports.some(s => s === 'strength') && (
          <div className="space-y-3 pl-3 border-l-2 border-[#9B59B6]/20">
            <div className="flex items-center gap-2">
              <Dumbbell className="h-3 w-3 text-[#9B59B6]" />
              <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
                {t('settings.trainingPreferences.sectionStrength')}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1 sm:col-span-2">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelStrengthGoal')}</span>
                <Select
                  value={prefs.strengthGoal}
                  onValueChange={(v) => setPrefs(p => ({ ...p, strengthGoal: v }))}
                >
                  <SelectTrigger className="font-mono text-sm border-[#9B59B6]/15 bg-white/[0.03] hover:border-[#9B59B6]/30 transition-colors">
                    <SelectValue placeholder="Select goal" />
                  </SelectTrigger>
                  <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
                    <SelectItem value="general" className="font-mono text-sm">{t('settings.trainingPreferences.strengthGeneral')}</SelectItem>
                    <SelectItem value="powerlifting" className="font-mono text-sm">{t('settings.trainingPreferences.strengthPowerlifting')}</SelectItem>
                    <SelectItem value="olympic" className="font-mono text-sm">{t('settings.trainingPreferences.strengthOlympic')}</SelectItem>
                    <SelectItem value="hypertrophy" className="font-mono text-sm">{t('settings.trainingPreferences.strengthHypertrophy')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelSquat1rm')}</span>
                <Input
                  type="number"
                  value={prefs.squat1rmKg ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, squat1rmKg: val }));
                  }}
                  placeholder="e.g. 120"
                  min={20}
                  max={500}
                  step={1}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelDeadlift1rm')}</span>
                <Input
                  type="number"
                  value={prefs.deadlift1rmKg ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, deadlift1rmKg: val }));
                  }}
                  placeholder="e.g. 150"
                  min={20}
                  max={500}
                  step={1}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelBench1rm')}</span>
                <Input
                  type="number"
                  value={prefs.bench1rmKg ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, bench1rmKg: val }));
                  }}
                  placeholder="e.g. 90"
                  min={20}
                  max={500}
                  step={1}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">{t('settings.trainingPreferences.sys1rmHint')}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── SECTION: Hyrox (shown only when hyrox is selected) ── */}
        {prefs.primarySports.some(s => s === 'hyrox') && (
          <div className="space-y-3 pl-3 border-l-2 border-[#FF4500]/20">
            <div className="flex items-center gap-2">
              <Timer className="h-3 w-3 text-[#FF4500]" />
              <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
                {t('settings.trainingPreferences.sectionHyrox')}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelHyroxCategory')}</span>
                <Select
                  value={prefs.hyroxCategory}
                  onValueChange={(v) => setPrefs(p => ({ ...p, hyroxCategory: v }))}
                >
                  <SelectTrigger className="font-mono text-sm border-[#FF4500]/15 bg-white/[0.03] hover:border-[#FF4500]/30 transition-colors">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
                    <SelectItem value="open" className="font-mono text-sm">{t('settings.trainingPreferences.hyroxOpen')}</SelectItem>
                    <SelectItem value="pro" className="font-mono text-sm">{t('settings.trainingPreferences.hyroxPro')}</SelectItem>
                    <SelectItem value="doubles" className="font-mono text-sm">{t('settings.trainingPreferences.hyroxDoubles')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelHyroxTargetFinish')}</span>
                <Input
                  type="number"
                  value={prefs.hyroxTargetFinishMinutes ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : Number(e.target.value);
                    setPrefs(p => ({ ...p, hyroxTargetFinishMinutes: val }));
                  }}
                  placeholder="e.g. 65"
                  min={30}
                  max={180}
                  step={1}
                  className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                />
                <p className="text-[9px] font-mono text-white/25 tracking-wider">
                  {prefs.hyroxTargetFinishMinutes
                    ? t('settings.trainingPreferences.sysHyroxTarget', { minutes: prefs.hyroxTargetFinishMinutes, hours: (prefs.hyroxTargetFinishMinutes / 60).toFixed(2) })
                    : t('settings.trainingPreferences.sysHyroxTargetHint')}
                </p>
              </div>
            </div>
            {/* Weak stations multi-select */}
            <div className="space-y-2">
              <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelWeakStations')}</span>
              <div className="flex flex-wrap gap-1.5">
                {HYROX_STATIONS.map((station) => {
                  const isWeak = prefs.hyroxWeakStations.includes(station.id);
                  return (
                    <button
                      key={station.id}
                      type="button"
                      onClick={() => setPrefs(p => ({
                        ...p,
                        hyroxWeakStations: isWeak
                          ? p.hyroxWeakStations.filter(s => s !== station.id)
                          : [...p.hyroxWeakStations, station.id],
                      }))}
                      className={cn(
                        'px-2.5 py-1 rounded-md border font-mono text-[9px] tracking-wider transition-all duration-200',
                        isWeak
                          ? 'border-[#FF4500]/50 bg-[#FF4500]/10 text-[#FF4500] shadow-[0_0_8px_rgba(255,69,0,0.15)]'
                          : 'border-white/[0.1] bg-white/[0.02] text-[#A0A0A0] hover:border-white/[0.2]'
                      )}
                    >
                      {station.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[9px] font-mono text-white/25 tracking-wider">
                {prefs.hyroxWeakStations.length > 0
                  ? t('settings.trainingPreferences.sysWeakStations', { count: prefs.hyroxWeakStations.length })
                  : t('settings.trainingPreferences.sysWeakStationsHint')}
              </p>
            </div>
          </div>
        )}

        {/* ── SECTION: Weekly Duty Cycle ── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
              {t('settings.trainingPreferences.sectionWeeklyDutyCycle')}
            </span>
            <span className="text-[9px] font-mono tabular-nums text-white/30">
              {activeDays}/7
            </span>
          </div>
          <div className="flex gap-2 justify-between">
            {dayLabels.map((label, index) => (
              <button
                key={index}
                onClick={() => toggleDay(index)}
                className={cn(
                  'w-10 h-10 rounded-full border-2 text-xs font-mono font-medium transition-all duration-200',
                  prefs.trainingDays[index]
                    ? 'border-[#00FFC8]/50 bg-[#00FFC8]/10 text-[#00FFC8] shadow-[0_0_10px_rgba(0,255,200,0.15)]'
                    : 'border-white/[0.1] bg-white/[0.02] text-[#A0A0A0] hover:border-white/[0.2]'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {!isOptimal && activeDays > 0 && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-[rgba(217,169,78,0.3)] bg-[rgba(217,169,78,0.06)]">
              <AlertTriangle className="h-3.5 w-3.5 text-[hsl(35,85%,55%)] mt-0.5 shrink-0" />
              <span className="text-[9px] font-mono tracking-wider text-[hsl(35,85%,55%)]">
                {t('settings.trainingPreferences.sysFrequencyRestricted')}
              </span>
            </div>
          )}
          {isOptimal && (
            <span className="text-[9px] font-mono tracking-[0.12em] uppercase text-[#00FFC8]">
              {t('settings.trainingPreferences.sysTemplateOptimal')}
            </span>
          )}
        </div>

        {/* ── SECTION: Weekly Volume ── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
              {t('settings.trainingPreferences.sectionWeeklyVolume')}
            </span>
            <span className="text-xs font-mono tabular-nums text-foreground">
              {prefs.hoursPerWeek}h
            </span>
          </div>
          <Slider
            value={[prefs.hoursPerWeek]}
            onValueChange={([value]) => setPrefs(p => ({ ...p, hoursPerWeek: value }))}
            min={3}
            max={25}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-[9px] font-mono text-white/25">
            <span>3h</span>
            <span>25h</span>
          </div>
        </div>

        {/* ── SECTION: Experience ── */}
        <div id="experience" className="space-y-2 scroll-mt-24">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
              {t('settings.trainingPreferences.sectionTrainingAge')}
            </span>
            <span className="text-xs font-mono tabular-nums text-foreground">
              {prefs.trainingAge}y
            </span>
          </div>
          <Slider
            value={[prefs.trainingAge]}
            onValueChange={([value]) => setPrefs(p => ({ ...p, trainingAge: value }))}
            min={0}
            max={30}
            step={1}
            className="w-full"
          />
        </div>

        {/* ── SECTION: Coaching Engine ── */}
        <div className="space-y-2">
          <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
            {t('settings.trainingPreferences.sectionCoachingEngine')}
          </span>
          <Select value={prefs.philosophy} onValueChange={(v) => setPrefs(p => ({ ...p, philosophy: v }))}>
            <SelectTrigger className="font-mono text-sm border-[#00FFC8]/15 bg-white/[0.03] hover:border-[#00FFC8]/30 transition-colors">
              <SelectValue placeholder="Select methodology" />
            </SelectTrigger>
            <SelectContent className="bg-[rgba(12,16,24,0.97)] backdrop-blur-[20px] border-white/[0.12] z-50">
              {getPhilosophyOptions(prefs.primarySports).map((p) => (
                <SelectItem key={p.value} value={p.value} className="font-mono text-sm">
                  {p.label} — <span className="text-muted-foreground">{p.desc}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[9px] font-mono text-white/25 tracking-wider">
            {t('settings.trainingPreferences.sysEngineHint')}
          </p>
        </div>

        {/* ── SECTION: Mission Objective ── */}
        <div id="race-target" className="space-y-3 scroll-mt-24">
          <div className="flex items-center gap-2">
            <Target className="h-3 w-3 text-[#00FFC8]" />
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
              {t('settings.trainingPreferences.sectionMissionObjective')}
            </span>
          </div>
          <Input
            value={prefs.goal}
            onChange={(e) => setPrefs(p => ({ ...p, goal: e.target.value }))}
            placeholder={t('settings.trainingPreferences.goalPlaceholder')}
            className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
          />

          {/* Training Focus Toggle */}
          <div className="flex gap-2">
            {(['race', 'general'] as const).map((focus) => (
              <button
                key={focus}
                onClick={() => setPrefs(p => ({ ...p, trainingFocus: focus }))}
                className={cn(
                  'flex-1 py-2 rounded-md border font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200',
                  prefs.trainingFocus === focus
                    ? 'border-[#00FFC8]/50 bg-[#00FFC8]/10 text-[#00FFC8]'
                    : 'border-white/[0.08] bg-white/[0.02] text-[#A0A0A0] hover:border-white/[0.15]'
                )}
              >
                {focus === 'race' ? t('settings.trainingPreferences.focusRace') : t('settings.trainingPreferences.focusGeneral')}
              </button>
            ))}
          </div>

          {/* Race Details (conditional) */}
          {prefs.trainingFocus === 'race' && (
            <div className="space-y-3 pl-3 border-l-2 border-[#00FFC8]/20">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelEventName')}</span>
                  <Input
                    value={prefs.targetEventName}
                    onChange={(e) => setPrefs(p => ({ ...p, targetEventName: e.target.value }))}
                    placeholder="Boston Marathon"
                    className="font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[9px] font-mono tracking-wider text-white/40 uppercase">{t('settings.trainingPreferences.labelEventDate')}</span>
                  <Input
                    type="date"
                    value={prefs.targetEventDate}
                    onChange={(e) => setPrefs(p => ({ ...p, targetEventDate: e.target.value }))}
                    className="font-mono text-sm border-white/[0.08] bg-white/[0.03]"
                  />
                </div>
              </div>
              <Textarea
                value={prefs.raceDetails}
                onChange={(e) => setPrefs(p => ({ ...p, raceDetails: e.target.value }))}
                placeholder={t('settings.trainingPreferences.raceDetailsPlaceholder')}
                className="min-h-[60px] font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
              />
            </div>
          )}
        </div>

        {/* ── SECTION: Injury Protocol ── */}
        <div id="injuries" className="space-y-3 scroll-mt-24">
          <div className="flex items-center gap-3">
            <Heart className="h-3 w-3 text-[hsl(35,85%,55%)]" />
            <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-muted-foreground">
              {t('settings.trainingPreferences.sectionInjuryProtocol')}
            </span>
            <Switch
              checked={prefs.hasInjuryHistory}
              onCheckedChange={(checked) => setPrefs(p => ({ ...p, hasInjuryHistory: checked }))}
            />
          </div>
          {prefs.hasInjuryHistory && (
            <Textarea
              placeholder={t('settings.trainingPreferences.injuryPlaceholder')}
              value={prefs.injuryNotes}
              onChange={(e) => setPrefs(p => ({ ...p, injuryNotes: e.target.value }))}
              className="min-h-[60px] font-mono text-sm border-white/[0.08] bg-white/[0.03] placeholder:text-white/20"
            />
          )}
        </div>

        {/* ── COMMIT ── */}
        <div className="pt-2">
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-mono text-[10px] tracking-[0.12em] uppercase transition-all duration-200 border active:scale-95',
              hasChanges
                ? 'border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.08)] text-[#00FFC8] hover:bg-[rgba(0,255,200,0.15)] hover:shadow-[0_0_16px_rgba(0,255,200,0.15)]'
                : 'border-white/[0.06] bg-white/[0.02] text-white/30 cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('settings.trainingPreferences.committing')}</>
            ) : (
              <><Save className="h-3.5 w-3.5" /> {t('settings.trainingPreferences.commitChanges')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
